import { VariantFinishEnum } from "@sorcery/db";
import { AccordionContent } from "@sorcery/ui";
import { LoadingSpinner } from "@sorcery/ui";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { RxMinus, RxPlus } from "react-icons/rx";
import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { useUser } from "~/providers/UserProvider";
import { deckJotai } from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import { api } from "~/utils/trpc";

type Props = {
  onCardHover?: (squiggle: { card: CardType; variant: VariantType }) => void;
  opened: boolean;
};

export const DeckHistorySection = ({ onCardHover, opened }: Props) => {
  const { user } = useUser();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;

  const { data: deckHistory, isLoading } = api.deck.getHistoryById.useQuery(
    { id: deck.id },
    { enabled: opened },
  );
  const { openModal } = useModal();

  const isAuthor = useMemo(
    () => !!user && !!deck.user && user.id === deck.user.id,
    [deck.user, user],
  );

  return (
    <AccordionContent>
      {isLoading ? (
        <div className="flex justify-start">
          <LoadingSpinner />
        </div>
      ) : !deckHistory || deckHistory.length === 0 ? (
        <p className="text-gray-500">No history available</p>
      ) : (
        deckHistory.map((group) => (
          <div key={group.label} className="mb-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-500">{group.label}</h3>
            <div className="border-l border-gray-500 pl-2">
              {group.changes.map((item) => (
                <div key={item.id} className="flex items-center">
                  <div
                    className="group flex cursor-pointer"
                    onMouseEnter={() =>
                      onCardHover?.({
                        card: item.card,
                        variant: item.variant,
                      })
                    }
                    onClick={() => {
                      isAuthor
                        ? openModal(ModalTypeEnum.DECKGALLERY, {
                            card: item.card,
                          })
                        : openModal(ModalTypeEnum.CARDGALLERY, {
                            card: item.card,
                          });
                    }}
                  >
                    <div className="flex items-center justify-center">
                      {item.quantity > 0 ? <RxPlus /> : <RxMinus />}
                    </div>
                    <div className="w-[15px] text-center font-medium">
                      {Math.abs(item.quantity)}
                    </div>
                    <div className="ml-2 flex-1">
                      <span
                        className={`group-hover:text-primary ${item.variant.finish === VariantFinishEnum.Foil ? "underline-sparkle relative" : ""}`}
                      >
                        {item.card.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </AccordionContent>
  );
};
